/* eslint-disable max-len */
import React, { useContext, useState, useEffect } from 'react';
import {
  oneOf,
  bool as boolType,
  any,
  shape as shapeType,
  string as stringType,
} from 'prop-types';
import { ExperienceContext, useConfigService } from '@thd-nucleus/experience-context';
import {
  bool,
  extend,
  string,
  shape,
  QueryProvider,
  params
} from '@thd-nucleus/data-sources';
import { ProductPod } from '@thd-olt-component-react/product-pod';
import {
  Col, Row, Image, Carousel
} from '@thd-olt-component-react/core-ui';
import classNames from 'classnames/bind';
import { LoadingPlaceholder } from './LoadingPlaceholder';
import { CarouselHeader } from './CarouselHeader';
import { RequiredAttributes } from './RequiredAttributes';
import { getSkippers, isAppliance } from '../utils/helpers';
import VisualTabs from './VisualTabs';
import BatteryImage from './BatteryImage';
import CollectionsImage from './CollectionsImage';
import '../../styles/thd-recs-containers.scss';
import { FallbackCard } from './FallbackCard';
import styles from './RecommendationsProductPodCarousel.module.scss';

// the QueryProvider on line 54 doesnt get access to ProductPod because data?.products is empty at first
// this is what hoist() was originally intended for but the problem with hoist() is that it mutates
// components and runs into issues when they are in multiple places
// eslint-disable-next-line react/prop-types
const cx = classNames.bind(styles);
const Wrapper = ({ children }) => children;
Wrapper.dataModel = extend({
  product: params({ itemId: string().isRequired() }).shape({
    dataSource: string(),
  })
}, ProductPod);
const RecommendationsProductPodCarousel = (props) => {
  const { channel } = useContext(ExperienceContext);

  const isMobile = channel === 'mobile';

  const {
    anchorImage,
    data,
    hideTitle,
    loading,
    scheme,
    showLoading,
    storeId,
    showFallback,
    fallbackInfo,
    userId,
    subtitle,
    isCategoryTab, // Displaying the category tabs
    // productPodProps,
    brandTitleMaxLine,
    customEventObject,
    hideATC,
    hideBulkPrice,
    hideFavorites,
    hideFulfillment,
    hideLimitPerOrder,
    hidePriceBadge,
    hideRating,
    hideRebates,
    hideSavingsText,
    hideShippingInfo,
    hideShopThisCollection,
    showSubscribeAndSave,
    zipCode,
    slidesPer,
    keepAddToCart,
    noATCFulfillment,
    hideSwatches
  } = props;

  let hideAddToCart;

  let recsCollectionTabCount = useConfigService('fs:recsCollectionTabCount') || 4;

  if (keepAddToCart === true) {
    hideAddToCart = false; // show ATC always - including MW
  } else if (hideATC || isMobile) {
    hideAddToCart = true;
  }

  const defaultVariables = {
    storeId,
    zipCode,
    installationServices: false,
    ...getSkippers({ skipSubscribeAndSave: !showSubscribeAndSave })
  };

  const { products, metadata, categories } = data || {};

  let title = metadata?.collectionName || metadata?.title || metadata?.batteryName;

  const [filtered, setFiltered] = useState([]);
  const [isCategorySelected, setIsCategorySelected] = useState(false);

  const pullFilteredData = (filteredProducts) => {
    setIsCategorySelected(true);
    setFiltered(filteredProducts);
  };

  let displayProducts;

  if (isCategorySelected) {
    displayProducts = filtered;
  } else {
    displayProducts = products;
  }

  let breakpoints = ({});
  if (!slidesPer) {
    breakpoints = ({
      sm: {
        slidesPerView: 2,
        slidesPerGroup: 2
      },
      md: {
        slidesPerView: 3,
        slidesPerGroup: 3
      },
      lg: {
        slidesPerView: metadata?.batteryImageUrl || anchorImage || metadata?.collectionImageUrl || isCategoryTab ? 4 : 6,
        slidesPerGroup: metadata?.batteryImageUrl || anchorImage || metadata?.collectionImageUrl || isCategoryTab ? 4 : 6
      },
      xl: {
        slidesPerView: metadata?.batteryImageUrl || anchorImage || metadata?.collectionImageUrl || isCategoryTab ? 4 : 6,
        slidesPerGroup: metadata?.batteryImageUrl || anchorImage || metadata?.collectionImageUrl || isCategoryTab ? 4 : 6
      }
    });
  } else if (slidesPer) {
    breakpoints = ({
      sm: {
        slidesPerView: slidesPer,
        slidesPerGroup: slidesPer
      },
      md: {
        slidesPerView: slidesPer,
        slidesPerGroup: slidesPer
      },
      lg: {
        slidesPerView: metadata?.batteryImageUrl || anchorImage || metadata?.collectionImageUrl || isCategoryTab ? 1 : slidesPer,
        slidesPerGroup: metadata?.batteryImageUrl || anchorImage || metadata?.collectionImageUrl || isCategoryTab ? 1 : slidesPer
      },
      xl: {
        slidesPerView: metadata?.batteryImageUrl || anchorImage || metadata?.collectionImageUrl || isCategoryTab ? 1 : slidesPer,
        slidesPerGroup: metadata?.batteryImageUrl || anchorImage || metadata?.collectionImageUrl || isCategoryTab ? 1 : slidesPer
      }
    });
  }

  let placeholderTxtLines;
  if (!isMobile && isCategoryTab) {
    placeholderTxtLines = 19;
  } else if (!isMobile && anchorImage) {
    placeholderTxtLines = 12;
  } else {
    placeholderTxtLines = 10;
  }

  return (
    <>
      {showLoading && loading && (
        showFallback && !userId && !products
          ? (
            <FallbackCard
              imageUrl={fallbackInfo.imageUrl}
              header={fallbackInfo.header}
              subheader={fallbackInfo.subheader}
              footerUrl={fallbackInfo.footerUrl}
              footer={fallbackInfo.footer}
            />
          )
          : <LoadingPlaceholder showMoreLines={(isCategoryTab)} txtPlaceholderLines={placeholderTxtLines} hideTitle={hideTitle} />
      )}
      {displayProducts?.length > 0 && (
        <>
          {!hideTitle && (
            isCategoryTab && !metadata?.collectionName ? (
              <CarouselHeader title="More From This Brand" />
            ) : (
              <CarouselHeader title={title} subtitle={subtitle} />
            )
          )}
          {!isMobile && anchorImage && (
            <Col xs="4" className={cx('carouselAnchorImage')}>
              <Image
                src={anchorImage}
                alt="main-product-image"
                height="400"
                width="400"
                data-testid="anchor-image"
              />
              <div className={cx('carouselAnchorImage__currentItem__label')}>Current Item</div>
            </Col>
          )}
          {isCategoryTab && (
            <Row>
              <Col xs="4">
                {metadata?.collectionTitle1 ? (
                  <h3 className="pipCollection-title">{metadata?.collectionTitle1}</h3>
                ) : (
                  <h3 className="pipCollection-title">{products[0]?.product?.identifiers?.brandName}</h3>
                )}
                {metadata?.collectionTitle2 && (
                  <h3 className="pipCollection-subtitle">{metadata?.collectionTitle2}</h3>
                )}
              </Col>
              {!isMobile && (
                <Col xs={metadata?.collectionImageUrl || anchorImage || isCategoryTab ? '8' : '12'}>
                  {isCategoryTab && categories?.length > 0 && categories?.length >= recsCollectionTabCount && (
                    // eslint-disable-next-line max-len
                    <VisualTabs products={products} categories={categories} filterItemIds={pullFilteredData} />
                  )}
                </Col>
              )}
            </Row>
          )}
          {metadata?.batteryImageUrl && (
            <BatteryImage metadata={metadata} scheme={scheme} isMobile={isMobile} />
          )}
          {(() => {
            if (metadata?.collectionImageUrl || isCategoryTab) {
              return (
                <CollectionsImage metadata={metadata} firstProductMedia={products[0]?.product?.media} isMobile={isMobile} />
              );
            }
            return null;
          })()}
          <Col xs={metadata?.batteryImageUrl || anchorImage || isCategoryTab || metadata?.collectionImageUrl ? '8' : '12'}>
            <QueryProvider
              dataSource={scheme}
              cacheKey={`${scheme}-recommendations-carousel`}
              defaultVariables={defaultVariables}
            >
              <Carousel
                breakpoints={breakpoints}
              >
                {displayProducts.map((dynamicProduct, idx) => {
                  const { product } = dynamicProduct;
                  const { itemId } = product;

                  return (
                    <Wrapper key={idx}>
                      <ProductPod
                        brandTitleMaxLine={brandTitleMaxLine}
                        customEventObject={customEventObject}
                        hideATC={hideAddToCart}
                        hideBulkPrice={hideBulkPrice}
                        hideFavorites={hideFavorites}
                        hideFulfillment={hideFulfillment}
                        hideLimitPerOrder={hideLimitPerOrder}
                        hidePriceBadge={hidePriceBadge}
                        hideRating={hideRating}
                        hideRebates={hideRebates}
                        hideSavingsText={hideSavingsText}
                        hideShippingInfo={hideShippingInfo}
                        hideShopThisCollection={hideShopThisCollection}
                        itemId={itemId}
                        key={itemId}
                        position={idx}
                        showSubscribeAndSave={showSubscribeAndSave}
                        showSeeMoreOptions={hideSwatches}
                        storeId={storeId}
                        noATCFulfillment={isAppliance(product) ? false : noATCFulfillment}
                      />
                    </Wrapper>
                  );
                }
                )}
              </Carousel>
            </QueryProvider>
          </Col>
        </>
      )}
    </>
  );
};

const productModel = extend({}, {
  itemId: string(),
  dataSource: string(),
}, ProductPod.dataModel.product);

export const wrappedProduct = shape({
  category: string(),
  description: string(),
  idEndDate: string(),
  idPromoDetails: string(),
  idStartDate: string(),
  isAnchor: bool(),
  product: params({
    dataSource: string()
  })
    .shape(productModel),
  // itemId: string(),
  slot: shape({
    description: string(),
    isRequired: bool(),
    name: string(),
  })
});

RecommendationsProductPodCarousel.dataModel = extend(Wrapper, wrappedProduct);

RecommendationsProductPodCarousel.displayName = 'RecommendationsProductPodCarousel';

RecommendationsProductPodCarousel.propTypes = {
  ...ProductPod.propTypes,
  // eslint-disable-next-line react/forbid-prop-types
  data: any,
  loading: boolType,
  hideTitle: boolType,
  scheme: stringType,
  showLoading: boolType,
  storeId: stringType.isRequired,
  subtitle: stringType,

  // productPodProps
  // product-pod take only these values for brandTitleMaxLine prop
  brandTitleMaxLine: oneOf([2, 3, null]),
  customEventObject: shapeType({
    component: stringType,
    customEvent: stringType
  }),
  hideATC: boolType,
  hideBulkPrice: boolType,
  hideFavorites: boolType,
  hideFulfillment: boolType,
  hideLimitPerOrder: boolType,
  hidePriceBadge: boolType,
  hideRating: boolType,
  hideRebates: boolType,
  hideSavingsText: boolType,
  hideShopThisCollection: boolType,
  hideShippingInfo: boolType,
  showSubscribeAndSave: boolType,
  keepAddToCart: boolType,
  noATCFulfillment: boolType,
  isCategoryTab: boolType,
  hideSwatches: boolType
};

RecommendationsProductPodCarousel.defaultProps = {
  hideTitle: false,
  scheme: 'drecs',
  showLoading: true,
  loading: false,
  data: null,
  subtitle: null,

  brandTitleMaxLine: null,
  customEventObject: null,
  hideATC: false,
  hideBulkPrice: false,
  hideFavorites: true,
  hideFulfillment: true,
  hideLimitPerOrder: true,
  hidePriceBadge: true,
  hideRating: false,
  hideRebates: true,
  hideSavingsText: true,
  hideShopThisCollection: true,
  hideShippingInfo: true,
  showSubscribeAndSave: false,
  keepAddToCart: null,
  noATCFulfillment: true,
  isCategoryTab: false,
  hideSwatches: true
};

export { RecommendationsProductPodCarousel };
